import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { NEUTRAL, onAccent } from './designTokens';
import { getDateStr } from './clientUtils';
import { initials } from './BookingDayBand';

// T-12: Kunder-listan. Mörkt huvud med sök, "Nya kunder" som tonat block, sedan alla
// kunder med nästa bokning till höger. Klick på rad öppnar kundkortet.

const DAYS_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const hm = (t) => (t || '').slice(0, 5);

function ClientsView({ therapist, clients, theme, clientUnreadMsg, isNewClient, onOpenClient, onMarkSeen, onAddClient, addForm }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('next'); // 'next' | 'name'
  const [nextByClient, setNextByClient] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const todayStr = getDateStr(new Date());
      const { data } = await supabase
        .from('bookings')
        .select('client_id, date, start_time, end_time')
        .eq('therapist_id', therapist.id)
        .neq('status', 'cancelled')
        .gte('date', todayStr)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      if (cancelled) return;
      const map = {};
      (data || []).forEach(b => { if (!map[b.client_id]) map[b.client_id] = b; });
      setNextByClient(map);
    })();
    return () => { cancelled = true; };
  }, [therapist.id, clients]);

  const now = new Date();
  const todayStr = getDateStr(now);
  const nowHM = now.toTimeString().slice(0, 5);
  const tomorrowStr = getDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));

  const nextLabel = (clientId) => {
    const b = nextByClient[clientId];
    if (!b) return { text: 'Ingen bokning', muted: true };
    if (b.date === todayStr) {
      if (hm(b.start_time) <= nowHM && hm(b.end_time) > nowHM) return { text: 'Pågår nu', accent: true };
      return { text: `Idag ${hm(b.start_time)}`, accent: true };
    }
    if (b.date === tomorrowStr) return { text: `Imorgon ${hm(b.start_time)}` };
    const d = new Date(b.date + 'T12:00:00');
    return { text: `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${hm(b.start_time)}` };
  };

  const term = search.trim().toLowerCase();
  const matches = (c) => !term || c.name.toLowerCase().includes(term) || (c.email && c.email.toLowerCase().includes(term)) || (c.phone && c.phone.includes(term));
  const sorted = clients.filter(matches).sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name, 'sv');
    const na = nextByClient[a.id], nb = nextByClient[b.id];
    if (na && nb) return (na.date + na.start_time).localeCompare(nb.date + nb.start_time);
    if (na) return -1;
    if (nb) return 1;
    return a.name.localeCompare(b.name, 'sv');
  });
  const newOnes = term ? [] : sorted.filter(isNewClient);
  const rest = term ? sorted : sorted.filter(c => !isNewClient(c));

  const unreadTotal = Object.values(clientUnreadMsg || {}).reduce((s, v) => s + v, 0);
  const newCount = clients.filter(isNewClient).length;
  const accentText = onAccent(theme.accent);
  const tint = theme.accent_soft_bg;

  const summary = [
    `${clients.length} ${clients.length === 1 ? 'kund' : 'kunder'}`,
    newCount > 0 ? <span key="n" style={{ color: tint }}>{newCount} {newCount === 1 ? 'ny' : 'nya'}</span> : null,
    unreadTotal > 0 ? `${unreadTotal} olästa ${unreadTotal === 1 ? 'meddelande' : 'meddelanden'}` : null,
  ].filter(Boolean);

  const joinedLabel = (c) => {
    if (!c.created_at) return null;
    const d = new Date(c.created_at);
    const ds = getDateStr(d);
    if (ds === todayStr) return 'anslöt idag';
    if (ds === getDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))) return 'anslöt igår';
    return `anslöt ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
  };

  const Row = ({ c, isNew, last }) => {
    const unread = clientUnreadMsg?.[c.id] || 0;
    const next = nextLabel(c.id);
    const meta = [c.email, c.phone, isNew ? joinedLabel(c) : null].filter(Boolean).join(' · ');
    return (
      <div
        onClick={() => onOpenClient(c)}
        className="fr-clients-row"
        style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 20px', borderBottom: last ? 'none' : `1px solid ${theme.card_border}`, background: isNew ? theme.accent_muted : 'transparent', cursor: 'pointer' }}
      >
        <div style={{ width: '38px', height: '38px', borderRadius: '999px', background: theme.accent_soft_bg, color: theme.accent_soft_text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: '700', flexShrink: 0 }}>
          {initials(c.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
            {unread > 0 && <span style={{ borderRadius: '999px', padding: '1px 7px', background: theme.accent, color: accentText, fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{unread > 9 ? '9+' : unread}</span>}
          </div>
          {meta && <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</div>}
        </div>
        <div className="fr-clients-next" style={{ fontSize: '12px', whiteSpace: 'nowrap', color: next.accent ? theme.accent_soft_text : next.muted ? NEUTRAL.faint : NEUTRAL.text, fontWeight: next.accent ? '600' : '400' }}>{next.text}</div>
        {isNew && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkSeen(c.id); }}
            style={{ background: 'transparent', border: `1px solid ${theme.card_border}`, color: NEUTRAL.text, padding: '6px 12px', borderRadius: theme.btn_radius, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
          >
            Hanterad ✓
          </button>
        )}
        <span aria-hidden="true" style={{ fontSize: '14px', color: NEUTRAL.line }}>›</span>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Mörkt huvud med sök */}
      <div className="fr-clients-hero" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: '24px 28px', color: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '-70px', width: '380px', height: '380px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div className="fr-clients-hero-inner" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '800', letterSpacing: '-0.01em', lineHeight: 1.1, color: '#FFFFFF', fontFamily: 'var(--font-heading)' }}>Kunder</h1>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', marginTop: '4px' }}>
              {summary.map((p, i) => <React.Fragment key={i}>{i > 0 && ' · '}{p}</React.Fragment>)}
            </div>
          </div>
          <button type="button" onClick={onAddClient} style={{ background: theme.hero_btn_bg, border: `1px solid ${theme.hero_btn_bg}`, color: theme.hero_btn_text, padding: '9px 14px', borderRadius: theme.btn_radius, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>+ Ny kund</button>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '10px', padding: '0 14px' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="4.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" /><path d="M10.5 10.5L14 14" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" /></svg>
          <input
            type="search"
            aria-label="Sök kund"
            placeholder="Sök på namn, e-post eller telefon…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, background: 'transparent', border: 'none', color: '#FFFFFF', padding: '12px 0', fontSize: '14px', outline: 'none', fontFamily: 'inherit', minWidth: 0 }}
          />
        </div>
      </div>

      {addForm}

      {/* Lista */}
      <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
        {newOnes.length > 0 && (
          <>
            <div style={{ padding: '10px 20px', background: theme.accent_muted, borderBottom: `1px solid ${theme.card_border}`, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: theme.accent }} />
              <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.accent_soft_text }}>Nya kunder · {newOnes.length}</span>
            </div>
            {newOnes.map(c => <Row key={c.id} c={c} isNew last={false} />)}
          </>
        )}

        <div style={{ padding: '10px 20px', borderBottom: `1px solid ${theme.card_border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted }}>{term ? `Träffar · ${rest.length}` : `Alla kunder · ${rest.length}`}</span>
          <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>
            Sortera:{' '}
            {[{ id: 'next', label: 'Nästa bokning' }, { id: 'name', label: 'Namn' }].map((o, i) => (
              <React.Fragment key={o.id}>
                {i > 0 && ' · '}
                <button type="button" onClick={() => setSort(o.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', color: sort === o.id ? theme.accent_soft_text : NEUTRAL.muted, fontWeight: sort === o.id ? '600' : '400' }}>{o.label}</button>
              </React.Fragment>
            ))}
          </span>
        </div>

        {rest.length === 0 ? (
          <div style={{ margin: '12px', padding: '24px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}`, fontSize: '12px', color: NEUTRAL.muted }}>
            {term ? 'Inga träffar · prova ett annat sökord' : clients.length === 0 ? 'Inga kunder ännu · klicka på + Ny kund' : 'Alla kunder är nya — se ovan'}
          </div>
        ) : rest.map((c, i) => <Row key={c.id} c={c} isNew={false} last={i === rest.length - 1} />)}
      </div>
    </div>
  );
}

export default ClientsView;
